/* eslint-disable complexity */
/* eslint-disable import/prefer-default-export */
import {
  useQueryClient,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from '@tanstack/react-query';
import { useUser } from '@unisporkal/user';
import { useExperience } from '@unisporkal/experiences';
import queryKeys from '../../support/uiStateKeys';
import acquisitionTypes, {
  type AcquisitionType,
  acquisitionTypeTitles,
} from '../../support/acquisitionTypes';
import acquisitionMode from '../../support/acquisitionMode';
import { useAsset } from './useAsset';
import useDownloadModifiedAsset from '../useDownloadModifiedAsset';
import { resolveSubscriptionAcquisitionType } from '../../shared/acquisitionTypeRules';
import type { AcquisitionTab } from '../../shared/acquisitionTypeRules/types';
import type { SupportedSubscriptions } from '../../../types/isomorphic/subscriptionClassification';
import { resolveSubscriptionOffer } from '../../../shared/models/subscriptionOffer';
import useRfSubscriptionStatusData from '../../shared/api/subscription/useRfSubscriptionStatusData';
import useSubscriptionInfoData from '../../shared/api/subscription/useSubscriptionInfoData';
import useDownloadAgreementsData from '../../shared/api/download/useDownloadAgreementsData';

type AvailableTypeStackItem = {
  type: AcquisitionType;
  title: string;
};

type AvailableTypesStack = Array<AvailableTypeStackItem>;

type AcquisitionTypeQueryData = {
  acquisitionType: AcquisitionType;
  acquisitionViewStack: Array<AcquisitionType>;
  availableTypesStack: Array<AvailableTypesStack>;
};

const dataKeys = [
  queryKeys.REMOVE_QUERY_ON_MODAL_CLOSE,
  queryKeys.ACQUISITION_TYPE,
  queryKeys.ACQUISITION_VIEW_STACK,
];

const gettySingleSeatSubsExperiences = ['ExpA', 'ExpB'];

/**
 * useAcquisitionType returns the acquisitionType and acquisitionViewStack for the view.
 * The acquisitionType can be any of the types in support/acquisitionTypes.ts
 * acquisitionViewStack is an array of views that can be stacked using the push and pop functions
 *
 * @returns {{
 *  acquisitionType: string,
 *  acquisitionViewStack: array,
 *  pushAcquisitionView: function,
 *  popAcquisitionView: function,
 *  setAcquisitionType: function
 *  }}
 */
export const useAcquisitionType = () => {
  const queryClient = useQueryClient();
  const user = useUser();
  const { asset, assets } = useAsset();
  const { genId, assetLicense, isUltrapackRedownloadable, hideALaCarte } =
    useDownloadModifiedAsset();
  const mode = queryClient.getQueryData([queryKeys.MODE]);
  const isModalView = mode === acquisitionMode.MODAL;
  const subscriptionAcquisitionTest = useExperience(
    'GettySingleSeatSubscription'
  );

  const isGettySingleSeatSubscriptionOfferActive =
    gettySingleSeatSubsExperiences.includes(
      subscriptionAcquisitionTest.activeOffer() ?? ''
    );

  const typedAsset = asset as {
    family?: string;
    id?: string;
    supportedSubscriptions?: SupportedSubscriptions;
    isRoyaltyFree?: () => boolean;
    isImage?: () => boolean;
    isCoveredByRF?: () => boolean;
  } | null;
  const rfSubscriptionStatus = useRfSubscriptionStatusData(typedAsset?.id);
  const subscriptionInfo = useSubscriptionInfoData(typedAsset?.family);
  const downloadAgreements = useDownloadAgreementsData(typedAsset?.id);
  const subscriptionOffer = resolveSubscriptionOffer(
    typedAsset?.supportedSubscriptions ?? null,
    subscriptionInfo?.eligibleSubscriptions ?? null
  );

  const getDefaultAcquisitionType = () => {
    const {
      isCoveredByPA,
      isDownloadable,
      isPurchaseable,
      isRestricted,
      isViewOnly,
      isPaViewOnly,
    } = asset as {
      isCoveredByPA: () => boolean;
      isDownloadable: () => boolean;
      isPurchaseable: () => boolean;
      isRestricted: () => boolean;
      isViewOnly: () => boolean;
      isPaViewOnly: () => boolean;
    };

    const isModified = !!genId;
    const isLicenseRedownload = !!assetLicense;

    const assetIsRestricted = isRestricted();
    const assetIsCoveredByPA = isCoveredByPA();
    const assetIsCoveredByRf = !!typedAsset?.isCoveredByRF?.();
    const assetIsDownloadable = isDownloadable();
    const assetIsPurchasable = isPurchaseable();
    const assetIsViewOnly = isViewOnly();
    const assetIsModified = isModified;
    const assetIsPaViewOnly = isPaViewOnly();

    const isSingleAsset = !Array.isArray(assets) || assets.length === 1;

    let resolvedAcquisitionTabs: AcquisitionTab[] | null = null;
    if (!isModified && isSingleAsset) {
      const { isAuthenticated, isRemembered } = user as {
        isAuthenticated: boolean;
        isRemembered: boolean;
      };
      resolvedAcquisitionTabs = resolveSubscriptionAcquisitionType({
        isAuthenticated,
        isRemembered,
        isSubscriptionExperienceActive:
          isGettySingleSeatSubscriptionOfferActive,
        hasRfSubscription: rfSubscriptionStatus?.hasRfSubscription ?? false,
        isSubscriptionEligible: subscriptionInfo?.isEligible ?? false,
        downloadAgreements: downloadAgreements ?? null,
        subscriptionOffer,
        assetIsDownloadable: assetIsDownloadable as boolean,
        assetIsPurchaseable: assetIsPurchasable as boolean,
        isCoveredByRf: assetIsCoveredByRf,
        isRfAsset: !!typedAsset?.isRoyaltyFree?.(),
        isImage: !!typedAsset?.isImage?.(),
        isRfRedownloadable: rfSubscriptionStatus?.rfRedownloadEligible ?? false,
      });
    }

    if (resolvedAcquisitionTabs) {
      // In modal view, suppress ALACARTE_LICENSE when the asset is already
      // downloadable. Subscription routing can yield [DOWNLOAD, ALACARTE], and
      // this keeps only DOWNLOAD to mirror the modal behavior that excludes
      // purchase tabs from the generic fallback path.
      const visibleTabs = isModalView
        ? resolvedAcquisitionTabs.filter(
            (tab) =>
              !(
                tab.type === acquisitionTypes.ALACARTE_LICENSE &&
                assetIsDownloadable
              )
          )
        : resolvedAcquisitionTabs;

      if (visibleTabs.length > 0) {
        return {
          acquisitionType: visibleTabs[0].type,
          acquisitionViewStack: [visibleTabs[0].type],
          availableTypesStack: [visibleTabs],
        };
      }
    }

    if (assets.length > 1) {
      return {
        acquisitionType: acquisitionTypes.BATCH_DOWNLOAD,
        acquisitionViewStack: [acquisitionTypes.BATCH_DOWNLOAD],
        availableTypesStack: [
          [
            {
              type: acquisitionTypes.BATCH_DOWNLOAD,
              title: acquisitionTypeTitles.BATCH_DOWNLOAD,
            },
          ],
        ],
      };
    }
    if (isModified && isLicenseRedownload && !isModalView) {
      const availableRedownloadTypes = [] as AvailableTypesStack;
      availableRedownloadTypes.push({
        type: acquisitionTypes.MODIFIED_LICENSE_REDOWNLOAD,
        title: acquisitionTypeTitles.MODIFIED_LICENSE_REDOWNLOAD,
      });
      availableRedownloadTypes.push({
        type: acquisitionTypes.MODIFIED_LICENSE,
        title: acquisitionTypeTitles.MODIFIED_LICENSE,
      });

      if (assetIsDownloadable) {
        availableRedownloadTypes.push({
          type: acquisitionTypes.MODIFIED_DOWNLOAD,
          title: acquisitionTypeTitles.DOWNLOAD,
        });
      }

      return {
        acquisitionType: acquisitionTypes.MODIFIED_LICENSE_REDOWNLOAD,
        acquisitionViewStack: [acquisitionTypes.MODIFIED_LICENSE_REDOWNLOAD],
        availableTypesStack: [availableRedownloadTypes],
      };
    }
    if (assetIsRestricted) {
      return {
        acquisitionType: acquisitionTypes.RESTRICTED,
        acquisitionViewStack: [acquisitionTypes.RESTRICTED],
        availableTypesStack: [
          [
            {
              type: acquisitionTypes.RESTRICTED,
              title: acquisitionTypeTitles.RESTRICTED,
            },
          ],
        ],
      };
    }
    if (user.isPaPreview && assetIsCoveredByPA) {
      return {
        acquisitionType: acquisitionTypes.PREVIEW,
        acquisitionViewStack: [acquisitionTypes.PREVIEW],
        availableTypesStack: [
          [
            {
              type: acquisitionTypes.PREVIEW,
              title: acquisitionTypeTitles.DOWNLOAD,
            },
          ],
        ],
      };
    }

    const availableTypes = [] as AvailableTypesStack;
    if (assetIsDownloadable && !assetIsModified) {
      availableTypes.push({
        type: acquisitionTypes.DOWNLOAD,
        title: acquisitionTypeTitles.DOWNLOAD,
      });

      if (assetIsPaViewOnly) {
        availableTypes.push({
          type: acquisitionTypes.LICENSE,
          title: acquisitionTypeTitles.LICENSE,
        });
      }
    } else if (
      (assetIsDownloadable || isUltrapackRedownloadable) &&
      assetIsModified
    ) {
      availableTypes.push({
        type: acquisitionTypes.MODIFIED_DOWNLOAD,
        title: acquisitionTypeTitles.DOWNLOAD,
      });

      if (assetIsPaViewOnly && !hideALaCarte) {
        availableTypes.push({
          type: acquisitionTypes.MODIFIED_LICENSE,
          title: acquisitionTypeTitles.MODIFIED_LICENSE,
        });
      }
    }

    if (assetIsPurchasable && !assetIsModified && !isModalView) {
      availableTypes.push({
        type: acquisitionTypes.LICENSE,
        title: acquisitionTypeTitles.LICENSE,
      });
    } else if (
      assetIsPurchasable &&
      assetIsModified &&
      !isModalView &&
      !isLicenseRedownload
    ) {
      availableTypes.push({
        type: acquisitionTypes.MODIFIED_LICENSE,
        title: acquisitionTypeTitles.MODIFIED_LICENSE,
      });
    }
    if (assetIsRestricted) {
      availableTypes.push({
        type: acquisitionTypes.RESTRICTED,
        title: acquisitionTypeTitles.RESTRICTED,
      });
    }

    if ((assetIsDownloadable || isUltrapackRedownloadable) && assetIsModified) {
      return {
        acquisitionType: acquisitionTypes.MODIFIED_DOWNLOAD,
        acquisitionViewStack: [acquisitionTypes.MODIFIED_DOWNLOAD],
        availableTypesStack: [availableTypes],
      };
    }

    if (assetIsViewOnly || assetIsDownloadable) {
      return {
        acquisitionType: acquisitionTypes.DOWNLOAD,
        acquisitionViewStack: [acquisitionTypes.DOWNLOAD],
        availableTypesStack: [availableTypes],
      };
    }

    if (assetIsPurchasable) {
      if (!isModalView && !assetIsModified) {
        return {
          acquisitionType: acquisitionTypes.LICENSE,
          acquisitionViewStack: [acquisitionTypes.LICENSE],
          availableTypesStack: [availableTypes],
        };
      }
      if (!isModalView && assetIsModified) {
        return {
          acquisitionType: acquisitionTypes.MODIFIED_LICENSE,
          acquisitionViewStack: [acquisitionTypes.MODIFIED_LICENSE],
          availableTypesStack: [availableTypes],
        };
      }
    }
    if (assetIsRestricted) {
      return {
        acquisitionType: acquisitionTypes.RESTRICTED,
        acquisitionViewStack: [acquisitionTypes.RESTRICTED],
        availableTypesStack: [
          [
            {
              type: acquisitionTypes.RESTRICTED,
              title: acquisitionTypeTitles.RESTRICTED,
            },
          ],
        ],
      };
    }
    return {
      acquisitionType: null,
      acquisitionViewStack: [],
      availableTypesStack: [],
    };
  };

  const { data }: UseSuspenseQueryResult<AcquisitionTypeQueryData> =
    useSuspenseQuery({
      queryKey: dataKeys,
      queryFn: ({ queryKey }) => queryClient.getQueryData(queryKey),
      initialData: () => {
        // Ensure asset is available at this point
        if (asset) {
          return getDefaultAcquisitionType();
        }
        return {
          acquisitionType: null,
          acquisitionViewStack: [],
          availableTypesStack: [],
        };
      },
    });

  const setAcquisitionType = (acquisitionType) => {
    queryClient.setQueryData(dataKeys, (oldData: AcquisitionTypeQueryData) => {
      const newViewStack = [...oldData.acquisitionViewStack];

      if (newViewStack.length > 0) {
        newViewStack[newViewStack.length - 1] = acquisitionType;
      } else {
        newViewStack.push(acquisitionType);
      }
      return {
        acquisitionType,
        acquisitionViewStack: newViewStack,
        availableTypesStack: oldData.availableTypesStack,
      };
    });
  };

  const pushAcquisitionView = (selectedAcquisitionType) => {
    const { isDownloadable } = asset;

    const assetIsDownloadable = isDownloadable();

    queryClient.setQueryData(dataKeys, (oldData: AcquisitionTypeQueryData) => {
      const acquisitionViewStack = [...oldData.acquisitionViewStack];
      const acquisitionType =
        acquisitionViewStack[acquisitionViewStack.length - 1];
      if (acquisitionType !== selectedAcquisitionType) {
        acquisitionViewStack.push(selectedAcquisitionType);
      }
      const availableTypesStack = [...oldData.availableTypesStack];
      if (selectedAcquisitionType === acquisitionTypes.ULTRA_PACK_UPSELL) {
        availableTypesStack.push([
          {
            type: acquisitionTypes.ULTRA_PACK_UPSELL,
            title: acquisitionTypeTitles.ULTRA_PACK_UPSELL,
          },
        ]);
      } else if (
        selectedAcquisitionType === acquisitionTypes.LIMITED_USE_PRODUCTS
      ) {
        availableTypesStack.push([
          {
            type: acquisitionTypes.LIMITED_USE_PRODUCTS,
            title: acquisitionTypeTitles.LIMITED_USE_PRODUCTS,
          },
        ]);
      } else if (
        selectedAcquisitionType === acquisitionTypes.CART_ADP_CONFIRMATION
      ) {
        availableTypesStack.push([
          {
            type: acquisitionTypes.CART_ADP_CONFIRMATION,
            title: acquisitionTypeTitles.CART_ADP_CONFIRMATION,
          },
        ]);
      } else if (
        selectedAcquisitionType === acquisitionTypes.MODIFIED_LICENSE
      ) {
        const availableTypes = [] as AvailableTypesStack;
        if (assetIsDownloadable) {
          availableTypes.push({
            type: acquisitionTypes.MODIFIED_DOWNLOAD,
            title: acquisitionTypeTitles.DOWNLOAD,
          });
        }
        availableTypes.push({
          type: acquisitionTypes.MODIFIED_LICENSE,
          title: acquisitionTypeTitles.MODIFIED_LICENSE,
        });
        availableTypesStack.push(availableTypes);
      }
      return {
        acquisitionType: selectedAcquisitionType,
        acquisitionViewStack,
        availableTypesStack,
      };
    });
  };

  const popAcquisitionView = () => {
    queryClient.setQueryData(dataKeys, (oldData: AcquisitionTypeQueryData) => {
      const newViewStack = [...oldData.acquisitionViewStack];
      const newAvailableTypesStack = [...oldData.availableTypesStack];

      if (newViewStack.length > 1) {
        newViewStack.pop();
      }

      if (newAvailableTypesStack.length > 1) {
        newAvailableTypesStack.pop();
      }
      const newAcquisitionType = newViewStack[newViewStack.length - 1];
      return {
        acquisitionType: newAcquisitionType,
        acquisitionViewStack: newViewStack,
        availableTypesStack: newAvailableTypesStack,
      };
    });
  };

  return {
    acquisitionViewStack: data.acquisitionViewStack,
    acquisitionType: data.acquisitionType,
    availableTypes: data.availableTypesStack
      ? data.availableTypesStack[data.availableTypesStack.length - 1]
      : [],
    subscriptionOffer,
    setAcquisitionType,
    pushAcquisitionView,
    popAcquisitionView,
  };
};
